import React from 'react';
import { ReactComponent as SignalIcon } from '@src/assets/icons/signal.svg';
import { ReactComponent as GpsIcon } from '@src/assets/icons/gps.svg';
import { EMPTY_STRING } from '@src/core/config';
import { arcToFixed } from '@src/core/extensions';
// (arcToFixed used for telemetry and mission %; header battery matches SelectedDrone)
import { ArcBatteryLife, ArcProgressBar, ArcStack, ArcText } from '@src/core/ui';
import { useFlightProvider } from '@src/features/order/provider';

import { EnRouteDroneInfoProps } from './types';

const EnRouteDroneInfo: React.FC<EnRouteDroneInfoProps> = (props) => {
  const { drone } = props;
  const { battery, gpsInfo, flightData } = useFlightProvider();
  const gpsStatus = gpsInfo?.gps_status;
  const gnssColor = gpsStatus?.gpsLost ? 'error.main' : 'white';
  const satellites = gpsStatus?.satellites ?? gpsInfo?.num_satellites;
  const fixType = gpsStatus?.fixType ?? gpsInfo?.fix_type;

  const [missionProgress, setMissionProgress] = React.useState<number>(0);
  const serial = drone?.serialNumber ?? '';
  const flightForDrone =
    flightData?.serialNumber && flightData.serialNumber === serial ? flightData : null;
  const batteryPercent = flightForDrone
    ? flightForDrone.batteryHealth
    : (drone?.batteryHealth ?? 0);

  React.useEffect(() => {
    if (serial && flightData?.serialNumber === serial && typeof flightData?.missionProgress === 'number') {
      const next = flightData.missionProgress;
      setMissionProgress((prev) => (prev !== next ? next : prev));
    }
  }, [flightData, serial]);

  React.useEffect(() => {
    setMissionProgress(0);
  }, [serial]);

  return (
    <ArcStack
      bgcolor="background.paper"
      marginTop={2}
      borderRadius="4px"
      padding="12px 14px 20px 14px"
      position="relative"
    >
      <ArcStack direction="row" justifyContent="space-between" alignItems="flex-start">
        <ArcStack spacing={0.4}>
          <ArcText.h5>{drone?.name ?? EMPTY_STRING}</ArcText.h5>
          <ArcText.caption fontSize={12} whiteSpace="nowrap">
            {drone?.serialNumber ?? EMPTY_STRING}
          </ArcText.caption>
        </ArcStack>

        <ArcStack direction="row" alignItems="center" spacing={0.5}>
          <ArcText.caption fontSize={12} whiteSpace="nowrap" color="white">
            {batteryPercent}%
          </ArcText.caption>
          <ArcBatteryLife percent={Number(batteryPercent) || 0} />
        </ArcStack>
      </ArcStack>

      <ArcStack direction="row" alignItems="center" justifyContent="space-between" marginTop={2}>
        <ArcStack>
          <ArcText.caption fontSize={8} whiteSpace="nowrap">
            SIGNAL
          </ArcText.caption>
          <ArcStack direction="row" alignItems="center" spacing={1}>
            <SignalIcon />
            <ArcText.caption fontSize={12} whiteSpace="nowrap" color="white">
              ---
            </ArcText.caption>
          </ArcStack>
        </ArcStack>
        <ArcStack>
          <ArcText.caption fontSize={8} whiteSpace="nowrap">
            GNSS
          </ArcText.caption>
          <ArcStack direction="row" alignItems="center" spacing={1}>
            <ArcText.caption fontSize={12} whiteSpace="nowrap" color={gnssColor}>
              {satellites}
            </ArcText.caption>
            <GpsIcon />
            <ArcText.caption fontSize={12} whiteSpace="nowrap" color={gnssColor}>
              {fixType}
            </ArcText.caption>
          </ArcStack>
        </ArcStack>
        <ArcStack>
          <ArcText.caption fontSize={8} whiteSpace="nowrap">
            VOLTAGE
          </ArcText.caption>
          <ArcStack direction="row" alignItems="center" spacing={1}>
            <ArcText.caption fontSize={12} whiteSpace="nowrap" color="white">
              {arcToFixed(battery?.voltage_v)} V
            </ArcText.caption>
          </ArcStack>
        </ArcStack>
        <ArcStack>
          <ArcText.caption fontSize={8} whiteSpace="nowrap">
            BATTERY EST.
          </ArcText.caption>
          <ArcStack direction="row" alignItems="center" spacing={1}>
            <ArcText.caption fontSize={12} whiteSpace="nowrap" color="white">
              {EMPTY_STRING}
            </ArcText.caption>
          </ArcStack>
        </ArcStack>
      </ArcStack>

      {!!drone?.currentOrderId && (
        <ArcStack marginTop={3}>
          <ArcProgressBar.mission value={missionProgress} />
          <ArcStack marginTop={1} direction="row" alignItems="center" justifyContent="space-between">
            <ArcText fontSize={14} whiteSpace="nowrap" color="white">
              Mission completion
            </ArcText>
            <ArcText fontSize={14} whiteSpace="nowrap" color="white">
              {parseFloat(arcToFixed(missionProgress, 1))}%
            </ArcText>
          </ArcStack>
        </ArcStack>
      )}
    </ArcStack>
  );
};

export default EnRouteDroneInfo;
